import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Users2, ArrowUpRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function InvestorExit() {
  const { t } = useLang();
  const exit = t.investorExit;

  return (
    <section id="investor-exit" className="py-32 md:py-40 relative overflow-hidden" style={{ background: '#0A0B0F' }}>
      {/* Background effects */}
      <div
        className="orb-gold absolute"
        style={{ width: 600, height: 600, top: '30%', left: '-10%', opacity: 0.12 }}
      />
      <div
        className="orb-purple absolute"
        style={{ width: 500, height: 500, bottom: '-10%', right: '-5%', opacity: 0.1 }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-[#9B7FFF] to-transparent" />
            <span className="text-[#9B7FFF] text-sm uppercase tracking-[0.2em] font-bold">Exit Strategy</span>
          </div>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 font-heading leading-[0.95]" style={{ fontFamily: "'Sora', sans-serif", textShadow: '0 2px 30px rgba(0,0,0,0.5)' }}>
            {exit.title}
          </h2>
          <p className="text-[#BFC3C9] text-xl md:text-2xl">{exit.subtitle}</p>
        </motion.div>

        {/* Exit Options Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {exit.options.map((option, i) => {
            const icons = [Building2, TrendingUp, Users2];
            const Icon = icons[i] || Building2;
            const colors = [
              { bg: 'rgba(212,175,55,0.1)', border: 'rgba(212,175,55,0.3)', icon: '#D4AF37' },
              { bg: 'rgba(107,78,255,0.1)', border: 'rgba(107,78,255,0.3)', icon: '#9B7FFF' },
              { bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', icon: '#22C55E' }
            ];
            const color = colors[i];

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-[2rem] p-10 card-lift group cursor-default"
                style={{
                  background: `linear-gradient(135deg, ${color.bg} 0%, ${color.bg.replace('0.1', '0.03')} 100%)`,
                  border: `1px solid ${color.border}`,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
                }}
              >
                {/* Hover glow */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{ 
                    background: `radial-gradient(circle at 50% 0%, ${color.bg.replace('0.1', '0.25')} 0%, transparent 70%)`
                  }}
                />

                {/* Number badge */}
                <div 
                  className="absolute top-8 right-8 w-12 h-12 rounded-xl flex items-center justify-center font-black text-2xl opacity-20"
                  style={{ color: color.icon }}
                >
                  {i + 1}
                </div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                    style={{ 
                      background: `linear-gradient(135deg, ${color.bg.replace('0.1', '0.2')} 0%, ${color.bg.replace('0.1', '0.08')} 100%)`,
                      border: `1px solid ${color.border}`,
                      boxShadow: `0 8px 24px ${color.bg.replace('0.1', '0.15')}`
                    }}
                  >
                    <Icon size={28} style={{ color: color.icon }} />
                  </div>

                  {/* Content */}
                  <p className="text-white text-xl md:text-2xl font-bold leading-tight">{option}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Valuation Highlight */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-12"
        >
          <div
            className="rounded-[2.5rem] p-12 text-center relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(107,78,255,0.08) 100%)',
              border: '1px solid rgba(212,175,55,0.3)',
              boxShadow: '0 30px 80px rgba(212,175,55,0.2)'
            }}
          >
            {/* Animated background */}
            <div
              className="absolute inset-0 opacity-40"
              style={{ 
                background: 'radial-gradient(circle at 50% 50%, rgba(212,175,55,0.3) 0%, transparent 70%)'
              }}
            />

            <div className="relative z-10">
              <div className="inline-flex items-center justify-center gap-2 mb-4">
                <ArrowUpRight size={24} className="text-[#D4AF37]" />
                <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-black">{exit.valuation.label}</p>
              </div>
              <p className="text-6xl md:text-7xl lg:text-8xl font-black font-heading mb-6" style={{ 
                background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4B8 50%, #D4AF37 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {exit.valuation.value}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Explanation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div
            className="glass rounded-2xl p-8 md:p-10"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <p className="text-[#E5E7EB] text-lg md:text-xl leading-relaxed">
              {exit.explanation}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
